// src/pages/PrivacyPolicyPage.jsx
import React, { useEffect, useState } from "react";
import NavBar from "../components/NavBar";

const MatrixPrivacyBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById("matrix-privacy-bg");
    const context = canvas.getContext("2d");
    let width = window.innerWidth,
      height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;
    const katakana = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";
    const latin = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const alphabet = katakana + latin + nums;
    const fontSize = 16;
    const columns = width / fontSize;
    const rainDrops = new Array(Math.floor(columns)).fill(1);
    const draw = () => {
      context.fillStyle = "rgba(0, 0, 0, 0.05)";
      context.fillRect(0, 0, width, height);
      context.fillStyle = "#0F0";
      context.font = fontSize + "px monospace";
      for (let i = 0; i < rainDrops.length; i++) {
        const text = alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        context.fillText(text, i * fontSize, rainDrops[i] * fontSize);
        if (rainDrops[i] * fontSize > height && Math.random() > 0.975) {
          rainDrops[i] = 0;
        }
        rainDrops[i]++;
      }
    };
    const interval = setInterval(draw, 30);
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return (
    <canvas
      id="matrix-privacy-bg"
      className="fixed top-0 left-0 w-full h-full opacity-20 pointer-events-none"
    />
  );
};

const PrivacyPolicyPage = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);
  return (
    <section className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
      <NavBar />
      <MatrixPrivacyBackground />
      <div className="relative z-10 container mx-auto px-4 py-16">
        {/* Card Container */}
        <div
          className={`max-w-4xl mx-auto bg-gray-800 bg-opacity-90 p-8 rounded-lg shadow-xl transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <header className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-extrabold text-green-400 mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-gray-300">
              Your privacy is critically important to AIOrbis. Please read our comprehensive Privacy Policy to understand how we collect, use, and protect your personal data.
            </p>
          </header>
          <article className="space-y-6 text-base leading-7 text-gray-200">
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">1. Introduction</h2>
              <p>
                Welcome to AIOrbis. We are dedicated to safeguarding your privacy and ensuring that your personal information is handled securely and responsibly. This Privacy Policy (“Policy”) explains how we collect, use, disclose, and protect your data when you access our website and services (“Services”). By using our Services, you agree to the practices described in this Policy.
              </p>
              <p>
                We view privacy as a fundamental right and commit to continuous improvement in our data protection practices. This document sets out our commitment to transparency and accountability.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">2. Definitions</h2>
              <p>
                For the purposes of this Policy, the following definitions apply:
              </p>
              <ul className="list-disc ml-6">
                <li><strong>"Personal Information":</strong> Any information that can be used to identify an individual, including names, email addresses, and telephone numbers.</li>
                <li><strong>"Usage Data":</strong> Information collected automatically regarding your use of our Services, such as IP address, browser type, and pages visited.</li>
                <li><strong>"Cookies":</strong> Small data files placed on your device to enhance your browsing experience.</li>
              </ul>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">3. Information We Collect</h2>
              <p>
                We may collect the following types of information:
              </p>
              <ul className="list-disc ml-6">
                <li><strong>Personal Information:</strong> Collected when you register, subscribe, or make a purchase. This may include your name, email address, phone number, and payment information.</li>
                <li><strong>Usage Data:</strong> Collected automatically when you interact with our Services, including the pages you visit and the actions you take.</li>
                <li><strong>Device Information:</strong> Information about the device you use to access our Services, such as IP address, browser type, and operating system.</li>
                <li><strong>Cookies and Tracking Technologies:</strong> Data collected via cookies and similar tools to enhance your user experience and for analytics purposes.</li>
              </ul>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">4. How We Use Your Information</h2>
              <p>
                The information we collect is used to:
              </p>
              <ul className="list-disc ml-6">
                <li>Provide, maintain, and improve our Services;</li>
                <li>Process transactions and manage your account;</li>
                <li>Personalize your experience;</li>
                <li>Analyze user behavior to enhance our offerings;</li>
                <li>Prevent and detect fraud or abuse;</li>
                <li>Comply with legal obligations.</li>
              </ul>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">5. Sharing and Disclosure</h2>
              <p>
                Your information may be shared under these circumstances:
              </p>
              <ul className="list-disc ml-6">
                <li><strong>With Service Providers:</strong> We share data with trusted partners who perform services on our behalf, such as payment processing and analytics.</li>
                <li><strong>For Legal Reasons:</strong> We may disclose information to comply with applicable laws, regulations, or legal processes.</li>
                <li><strong>Business Transfers:</strong> In the event of a merger or sale, your information may be transferred, subject to this Policy.</li>
                <li><strong>With Your Consent:</strong> We may share data when you have given explicit permission.</li>
              </ul>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">6. Data Security and Retention</h2>
              <p>
                We implement robust security measures to protect your data; however, no online transmission is entirely secure. We retain your data only as long as necessary to fulfill the purposes for which it was collected or to comply with legal obligations.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">7. Your Rights</h2>
              <p>
                Depending on your location, you may have the right to:
              </p>
              <ul className="list-disc ml-6">
                <li>Access your personal data;</li>
                <li>Request corrections or updates;</li>
                <li>Request deletion or restriction of your data;</li>
                <li>Object to the processing of your data;</li>
                <li>Data portability rights.</li>
              </ul>
              <p>
                To exercise these rights, please contact us using the details provided below.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">8. International Data Transfers</h2>
              <p>
                Your information may be transferred and stored outside your country. We take appropriate measures to ensure that your data remains protected in accordance with this Policy.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">9. Children's Privacy</h2>
              <p>
                Our Services are not directed to individuals under the age of 16. We do not knowingly collect data from children under 16. If you believe we have collected data from a child, please contact us immediately.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy periodically. Any changes will be posted on our website with an updated effective date. We encourage you to review the Policy regularly.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">11. Contact Information</h2>
              <p>
                If you have any questions about this Policy, please contact us at:
              </p>
              <p>
                <strong>AIOrbis</strong><br />
                Email: privacy@aiorbis.com<br />
                Address: 1234 Innovation Drive, Tech City, Country
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">12. Additional Disclosures</h2>
              <p>
                We continuously audit our systems to maintain compliance with global data protection standards such as the GDPR and CCPA. In the event of a data breach, we will notify affected users in accordance with legal requirements and take prompt corrective action.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicyPage;
